import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Button, List,
  ListItem, ListItemText, CircularProgress, Alert
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../../config/api';
import { normalizarUrlArquivo } from '../../../utils/fileUrl';

const ArquivoItem = ({ arquivo, clienteUuid }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  
  const tipo = arquivo.tipo || 'desconhecido';
  const dataEnvio = arquivo.data_envio ? new Date(arquivo.data_envio).toLocaleString() : 'Data desconhecida';

  // Função para buscar URL assinada do Spaces
  const buscarUrlAssinada = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/clientes/${clienteUuid}/arquivos/${arquivo.id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const urlArquivo = normalizarUrlArquivo(res.data.url);
      console.log('✅ URL de arquivo gerada:', urlArquivo);
      setUrl(urlArquivo);
    } catch (err) {
      console.error('❌ Erro ao buscar URL:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar URL ao montar componente
  useEffect(() => {
    buscarUrlAssinada();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#fafafa' }}>
      <ListItemText
        primary={<strong>{arquivo.nome}</strong>}
        secondary={`Tipo: ${tipo} | Enviado em: ${dataEnvio}`}
      />

      {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}

      {!loading && url && tipo.startsWith('image/') && (
        <img
          src={url}
          alt={arquivo.nome}
          style={{
            width: '100%',
            maxHeight: 400,
            marginTop: 10,
            objectFit: 'contain',
            borderRadius: 8
          }}
          onError={(e) => {
            console.error('❌ Erro ao carregar imagem:', url);
            e.target.style.display = 'none';
          }}
        />
      )}

      {!loading && url && tipo === 'application/pdf' && (
        <iframe
          src={url}
          title={arquivo.nome}
          width="100%"
          height="500px"
          style={{ border: '1px solid #ccc', marginTop: 10, borderRadius: 8 }}
        />
      )}

      {!loading && url && !tipo.startsWith('image/') && tipo !== 'application/pdf' && (
        <Button
          variant="outlined"
          href={url}
          target="_blank"
          sx={{
            mt: 2,
            borderColor: '#f78b00',
            color: '#f78b00',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#f78b00',
              color: '#fff'
            }
          }}
        >
          Visualizar / Download
        </Button>
      )}
    </ListItem>
  );
};

export default function VisualizarCliente() {
  const { uuid: uuid_cliente } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCliente = async () => {
      const token = localStorage.getItem('token');
      console.log('🔍 uuid_cliente recebido via URL:', uuid_cliente);
      console.log('📦 Token do localStorage:', token);

      if (!uuid_cliente) {
        setError('UUID do cliente está inválido.');
        setLoading(false);
        return;
      }

      try {
        const resCliente = await axios.get(`${API_URL}/clientes/${uuid_cliente}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Cliente carregado:', resCliente.data);
        setCliente(resCliente.data);

        const resArquivos = await axios.get(`${API_URL}/clientes/${uuid_cliente}/arquivos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('📂 Arquivos carregados:', resArquivos.data);
        setArquivos(resArquivos.data || []);
      } catch (err) {
        console.error('❌ Erro ao buscar dados:', err);
        setError('Erro ao buscar os dados do cliente.');
      } finally {
        setLoading(false);
      }
    };

    fetchCliente();
  }, [uuid_cliente]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!cliente || !cliente.nome) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Alert severity="warning">Cliente não encontrado ou dados incompletos.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        <Box sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {/* HEADER GRADIENT */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: '#fff'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', flex: 1 }}>
              👁️ VISUALIZAR CLIENTE
            </Typography>
          </Box>

          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* SEÇÃO 1: DADOS PESSOAIS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                👤 DADOS PESSOAIS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>NOME COMPLETO</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{cliente?.nome}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>CPF/CNPJ</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{cliente?.cpf_cnpj}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SEÇÃO 2: CONTATO */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📞 CONTATO
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>TELEFONE</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{cliente?.telefone}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>EMAIL</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500, wordBreak: 'break-all' }}>{cliente?.email}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SEÇÃO 3: ENDEREÇO */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📍 ENDEREÇO
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 200px' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>ENDEREÇO</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{cliente?.endereco}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>UF</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{cliente?.uf}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SEÇÃO 4: ARQUIVOS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📎 ARQUIVOS ENVIADOS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0'
              }}>
                {arquivos.length === 0 ? (
                  <Typography color="textSecondary">Nenhum arquivo enviado.</Typography>
                ) : (
                  <List disablePadding>
                    {arquivos.map((arquivo) => (
                      <ArquivoItem key={arquivo.id} arquivo={arquivo} clienteUuid={uuid_cliente} />
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* BOTÕES */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/cadastros/clientes')}
                sx={{
                  borderColor: '#999',
                  color: '#999',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                VOLTAR
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate(`/cadastros/clientes/${uuid_cliente}/editar`)}
                sx={{
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d0d0d 0%, #e67800 100%)'
                  }
                }}
              >
                EDITAR
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
